import merge from 'lodash/merge';

import {
  CareerFeedComponent,
  type GetCareerFeedQuery,
  type GetCareerFeedQueryVariables,
} from 'src/graphql/graphql';

export type CareerFeed = NonNullable<
  NonNullable<GetCareerFeedQuery['careerFeed']>
>;

type CareerFeedItem = NonNullable<
  NonNullable<CareerFeed['edges']>[number]
>['node'];

type CareerFeedJob = Extract<CareerFeedItem, { __typename: 'CareerFeedJob' }>;
type CaeerFeedCompanyEvp = Extract<
  CareerFeedItem,
  { __typename: 'CareerFeedCompanyEvp' }
>;

const DEFAULT_COMPANY_ID = '162588138056498';

export const mockFeedCompanyEvp = (
  components: CareerFeedComponent[],
  companyEvp: Partial<CaeerFeedCompanyEvp> = {},
): CaeerFeedCompanyEvp | undefined => {
  const shouldInclude = components.includes(CareerFeedComponent.CompanyEvp);
  if (!shouldInclude) {
    return undefined;
  }

  return merge(
    {
      __typename: 'CareerFeedCompanyEvp',
      id: companyEvp.id || DEFAULT_COMPANY_ID,
      companyId: companyEvp.id || DEFAULT_COMPANY_ID,
      companyReviewsAISummary: {
        aISummaryContent: {
          positiveAndChallengesSummary:
            "Australia's only challenger bank purpose-built for small and medium business, we exist to help organisations secure the lending they need and the service they deserve. Our relationship model places a priority on highly skilled, knowledgeable and imaginative relationship bankers who take the time to get to know our customers and their business. We work closely with our customers to provide banking expertise based on a deep understanding of their business's opportunities and potential.",
        },
      },
      companyProfile: {
        data: {
          canonicalUrl: '/companies/judo-bank-162588138056498',
          name: 'Judo Bank',
          overview: {
            size: '101-1,000',
            industry: 'Banking & Financial Services',
          },
          branding: {
            cover:
              'https://bx-branding-gateway.cloud.seek.com.au/b8581617-de9e-4c0e-aa3a-7bc8152a979e/jdpCover',
            logo: 'https://bx-branding-gateway.cloud.seek.com.au/b8581617-de9e-4c0e-aa3a-7bc8152a979e/jdpLogo',
          },
        },
      },
    },
    companyEvp,
  );
};

const DEFAULT_JOB_ID = '29663948';

export const mockFeedJob = (
  careerFeedJob: Partial<CareerFeedJob> = {},
  {
    includeBadges = true,
    includeSavedJob = false,
  }: { includeBadges?: boolean; includeSavedJob?: boolean } = {},
): CareerFeedJob =>
  merge(
    {
      __typename: 'CareerFeedJob',
      jobId: careerFeedJob.job?.id || DEFAULT_JOB_ID,
      id: careerFeedJob.job?.id || DEFAULT_JOB_ID,
      savedJob: includeSavedJob
        ? {
            id: '123456789',
          }
        : null,
      ...(includeBadges
        ? {
            jobBadges: {
              badgeDetails: [
                {
                  displayText: 'New to you',
                  badge: 'NEW',
                },
                {
                  displayText: 'Viewed',
                  badge: 'VIEWED',
                },
                {
                  displayText: 'Expiring soon',
                  badge: 'EXPIRES_SOON',
                },
                {
                  displayText: 'Be an early applicant',
                  badge: 'EARLY_APPLICANT',
                },
                {
                  displayText: 'Strong applicant',
                  badge: 'TOP_APPLICANT',
                },
              ],
            },
          }
        : {}),

      job: {
        __typename: 'Job',
        advertiser: {
          __typename: 'Advertiser',
          name: 'Surge Direct Pty Ltd',
        },
        id: DEFAULT_JOB_ID,
        title: 'Pastry Chef & Chef de Partie',
        location: {
          label: 'Cairns, Cairns & Far North QLD',
        },
        salary: null,
        listedAt: {
          label: '1h ago',
        },
        products: {
          branding: {
            logo: {
              url: 'https://image-service-cdn.seek.com.au/7681d821cee6efb70961d6436430839eac077c1e/f3c5292cec0e05e4272d9bf9146f390d366481d0',
            },
          },
        },
      },
      solMetadata: {
        token: '1~201b49e4-d75c-4e74-a267-cdf652e36e77',
        jobId: careerFeedJob.job?.id || DEFAULT_JOB_ID,
        tags: {
          'recs:recSysChannel': '',
          'recs:product': 'recommended_jobs_page',
          'recs:experimentGroup': '51',
          'recs:flight': '51',
        },
      },
    },
    careerFeedJob,
  );

const getDefaultJob = (index: number): CareerFeedJob =>
  mockFeedJob(
    {
      job: {
        id: `111${index}`,
        title: `Not Boring Job ${index}`,
        advertiser: {
          __typename: 'Advertiser',
          name: `Cool company ${index}`,
        },
        location: {
          label: `Cool location ${index}`,
        },
      },
    },
    {
      // Make every 5th job saved
      includeSavedJob: index % 5 === 0,
      // Make every 7th job include badges
      includeBadges: index % 7 === 0,
    },
  );

export const getMockFeedItems = (
  components: CareerFeedComponent[] = [],
): Array<CareerFeedItem | undefined> => [
  mockFeedJob(),
  mockFeedJob({
    savedJob: {
      id: '123456789',
    },
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'OnCue Talent',
      },
      id: '29752529',
      title: "EXTRA's Wanted for TV Commercials, TV Series & Film in Melbourne",
      salary: {
        label: '$25.66 p/hr + 11.5% Super + NFP Salary Packaging',
      },
      location: {
        label: 'Oakleigh South, Victoria',
      },
      listedAt: {
        label: '2d ago',
      },
      products: {
        branding: {
          logo: {
            url: 'https://image-service-cdn.seek.com.au/2e92eb70e5c5adfdc1e0ac590b3c64debd9dc3a4/f3c5292cec0e05e4272d9bf9146f390d366481d0',
          },
        },
      },
    },
  }),
  mockFeedJob({
    savedJob: {
      id: '123456789',
    },
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'Spirit',
      },
      id: '29677777',
      title:
        'No Experience Needed - Apply today start tomorrow - Full Time Appointment Setter',
      salary: {
        label: '$90,000 – $110,000 per year',
      },
      location: {
        label: 'North Sydney, Sydney NSW',
      },
      listedAt: {
        label: '9d ago',
      },
      products: {
        branding: {
          logo: {
            url: 'https://image-service-cdn.seek.com.au/f318d46b3b07d6d90a8f4120c48defbc7379a208/f3c5292cec0e05e4272d9bf9146f390d366481d0',
          },
        },
      },
    },
  }),
  mockFeedCompanyEvp(components),
  mockFeedJob({
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'Charterhouse Professional Support',
      },
      id: '29752529',
      title: 'Senior EA/Business Manager $110,000 package',
      salary: {
        label: '$110,000 package',
        currencyLabel: 'MYR',
      },
      location: {
        label: 'Sydney',
      },
      listedAt: {
        label: '1d ago',
      },
      products: {
        branding: {
          logo: {
            url: 'https://image-service-cdn.seek.com.au/3585866db3300b0803a44f1491f50ac457b6f116/dffceafa8da80ae865f1fa835a87a74a26be6546',
          },
        },
      },
    },
  }),
  mockFeedJob({
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'Jobstreet',
      },
      id: '999',
      title: 'Error State for Job Detail',
      salary: {
        label: '$80,000 – $1,000,000 per year',
        currencyLabel: 'MYR',
      },
      location: {
        label: 'Kuala Lumpur, Malaysia',
      },
      listedAt: {
        label: '30d ago',
      },
      products: null,
    },
  }),
  mockFeedCompanyEvp(components, {
    id: '432953',
    companyId: '432953',
    companyReviewsAISummary: {
      aISummaryContent: {
        positiveAndChallengesSummary:
          "Bendigo & Adelaide Bank receives consistent praise for its <strong>supportive team environment</strong> and <strong>collegial atmosphere</strong> among immediate colleagues. The bank offers attractive <strong>flexible working arrangements</strong>, including hybrid work options and adaptable hours, which contribute to good work-life balance. Employees also appreciate the opportunity to help <strong>customers and contribute to the community</strong>. <br><br>However, employees face several challenges, including working with <strong>multiple outdated computer systems</strong> that create inefficiencies. There are concerns about <strong>leadership communication</strong> and decision-making, particularly regarding organisational changes. Staff also note increasing <strong>pressure to meet sales targets</strong>, which some feel conflicts with the bank's traditional customer-first approach. Additionally, some employees mention limited <strong>opportunities for career advancement</strong> and professional development within the organisation.",
      },
    },
    companyProfile: {
      data: {
        canonicalUrl: '/companies/bendigo-n-adelaide-bank-432953',
        name: 'Bendigo & Adelaide Bank',
        overview: {
          size: '5,001-10,000',
          industry: 'Banking & Financial Services',
        },
        branding: {
          cover: null, // Intentionally left blank to simulate missing cover image
          logo: 'https://image-service-cdn.seek.com.au/a5d61974c56a5518977f368f72d5b1aec280b815/f3c5292cec0e05e4272d9bf9146f390d366481d0',
        },
      },
    },
  }),
  ...Array(44)
    .fill(null)
    .map((_, index) => getDefaultJob(index)),
];

export const mockCareerFeedQueryResponse = ({
  args,
  limit = 100,
}: {
  args?: GetCareerFeedQueryVariables;
  limit?: number;
}) => {
  const feedItems = getMockFeedItems(args?.components as CareerFeedComponent[])
    .filter(Boolean)
    .slice(0, limit);
  return {
    __typename: 'CareerFeed' as const,
    pageInfo: {
      __typename: 'CareerFeedPageInfo' as const,
      cursor: 'cursor',
      count: feedItems.length,
    },
    solMetadata: {},
    edges: [
      ...feedItems.map((feedItem) => ({
        node: feedItem,
        __typename: 'CareerFeedEdge' as const,
      })),
    ],
  };
};
